import { inject, signal } from '@angular/core';
import type { HubConnection } from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { HubConnectionFactory } from './hub-connection.factory';

export type HubClientState =
  | 'disabled'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'offline';

export abstract class HubClientBase {
  private readonly connectionFactory = inject(HubConnectionFactory);

  private connection: HubConnection | null = null;
  private startPromise: Promise<void> | null = null;
  // Bumped by every connect/disconnect so a startup finishing after a disconnect sees it is stale.
  private generation = 0;

  private readonly _reconnected = new Subject<void>();

  readonly state = signal<HubClientState>('disabled');

  protected abstract readonly hubName: string;

  protected abstract bindServerMethods(connection: HubConnection): void;

  get reconnected$(): Observable<void> {
    return this._reconnected.asObservable();
  }

  connect(): Promise<void> {
    this.startPromise ??= this.startConnection(++this.generation);
    return this.startPromise;
  }

  async disconnect(): Promise<void> {
    const connection = this.connection;
    this.generation++;
    this.connection = null;
    this.startPromise = null;
    this.state.set('disabled');

    await connection?.stop();
  }

  protected async invoke(
    methodName: string,
    ...args: readonly unknown[]
  ): Promise<void> {
    await this.invokeWithResult<unknown>(methodName, ...args);
  }

  protected async invokeWithResult<T>(
    methodName: string,
    ...args: readonly unknown[]
  ): Promise<T | null> {
    const connection = this.connection;
    if (connection === null || this.state() !== 'live') {
      return null;
    }

    try {
      return await connection.invoke<T>(methodName, ...args);
    } catch {
      return null;
    }
  }

  private async startConnection(generation: number): Promise<void> {
    this.state.set('connecting');

    try {
      const connection = await this.connectionFactory.create(this.hubName);
      if (!connection) {
        this.state.set('disabled');
        return;
      }

      if (generation !== this.generation) {
        return;
      }

      this.connection = connection;
      this.registerHandlers(connection);
      await connection.start();

      if (generation !== this.generation) {
        await connection.stop();
        return;
      }
      this.state.set('live');
    } catch {
      if (generation === this.generation) {
        this.startPromise = null;
        this.state.set('offline');
      }
    }
  }

  private registerHandlers(connection: HubConnection): void {
    this.bindServerMethods(connection);

    connection.onreconnecting(() => this.state.set('reconnecting'));
    connection.onreconnected(() => {
      this.state.set('live');
      this._reconnected.next();
    });
    connection.onclose(() => this.state.set('offline'));
  }
}
